import { apiRequest } from './apiClient';
import { normalizeSidebarLayout, SidebarLayout } from '../components/sidebarLayout';

/**
 * Workspace-wide sidebar settings chosen by admins: renamed entries (e.g. "Contacts" shown as
 * "Patients") and the layout people start from before they customize their own.
 */
export type WorkspaceNavigationSettings = {
  labelOverrides: Record<string, string>;
  defaultLayout: SidebarLayout | null;
  updatedAt: string | null;
};

export type UpdateWorkspaceNavigationSettingsRequest = {
  labelOverrides: Record<string, string>;
  defaultLayout: SidebarLayout | null;
};

export const EMPTY_WORKSPACE_NAVIGATION_SETTINGS: WorkspaceNavigationSettings = {
  labelOverrides: {},
  defaultLayout: null,
  updatedAt: null,
};

const normalizeLabelOverrides = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const result: Record<string, string> = {};
  for (const [key, label] of Object.entries(value as Record<string, unknown>)) {
    if (typeof label === 'string' && label.trim()) {
      result[key] = label.trim();
    }
  }
  return result;
};

export const normalizeWorkspaceNavigationSettings = (payload: any): WorkspaceNavigationSettings => ({
  labelOverrides: normalizeLabelOverrides(payload?.labelOverrides),
  defaultLayout: payload?.defaultLayout && typeof payload.defaultLayout === 'object'
    ? normalizeSidebarLayout(payload.defaultLayout)
    : null,
  updatedAt: typeof payload?.updatedAt === 'string' ? payload.updatedAt : null,
});

export const workspaceNavigationService = {
  get: async (): Promise<WorkspaceNavigationSettings> => {
    const payload = await apiRequest<any>('/api/v1/workspace-navigation');
    return normalizeWorkspaceNavigationSettings(payload);
  },

  /** Full replacement; admin only. */
  update: async (request: UpdateWorkspaceNavigationSettingsRequest): Promise<WorkspaceNavigationSettings> => {
    const payload = await apiRequest<any>('/api/v1/workspace-navigation', {
      method: 'PUT',
      body: JSON.stringify(request),
    });
    return normalizeWorkspaceNavigationSettings(payload);
  },
};
